/**
 * ImageUpload — Reusable image upload component with preview
 * Uses /api/uploads/image endpoint
 */
import React, { useState, useRef } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ImageUpload({ value, onChange, folder = 'general', label, className = '' }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    setError('');
    setUploading(true);

    const form = new FormData();
    form.append('file', file);

    try {
      const res = await fetch(`${API}/api/uploads/image?folder=${folder}`, {
        method: 'POST',
        body: form,
        headers: getBranchAuthHeaders(),
      });
      const data = await res.json();
      if (data.success && data.url) {
        onChange(data.url);
      } else {
        setError('Upload failed');
      }
    } catch {
      setError('Upload failed. Try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const clear = () => {
    onChange('');
    setError('');
  };

  const previewSrc = value
    ? value.startsWith('/api/') ? `${API}${value}` : value
    : null;

  return (
    <div className={className} data-testid="image-upload">
      {label && <label className="block text-sm font-medium mb-1.5">{label}</label>}
      {previewSrc ? (
        <div className="relative group w-full h-36 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
          <img
            src={previewSrc}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <button
            type="button"
            onClick={clear}
            className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="image-upload-clear"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full h-36 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-gray-700 bg-gray-50"
          data-testid="image-upload-trigger"
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm">Uploading...</span>
            </>
          ) : (
            <>
              <Upload className="w-6 h-6" />
              <span className="text-sm">Click to upload image</span>
              <span className="text-xs text-gray-400">PNG, JPG, WebP up to 5MB</span>
            </>
          )}
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        data-testid="image-upload-input"
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
